import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaClock, 
  FaTrash, 
  FaToggleOn, 
  FaToggleOff,
  FaSpinner,
  FaCalendarAlt,
  FaArrowUp,
  FaArrowDown,
  FaSync,
  FaCheckCircle,
  FaInfoCircle
} from 'react-icons/fa';
import api from '../api';

const ScheduledScansSection = ({ refreshTrigger = 0 }) => {
  const [scheduledScans, setScheduledScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchScheduledScans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/fno/scheduled-scans');
      setScheduledScans(response.data);
      setLastRefresh(new Date());
      console.log('✅ Scheduled scans fetched:', response.data);
    } catch (err) {
      console.error('❌ Failed to fetch scheduled scans:', err);
      setError(err.response?.data?.detail || 'Failed to load scheduled scans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScheduledScans();
  }, [refreshTrigger]);
  
  // Auto-refresh every 30 seconds to show updated scan status
  useEffect(() => {
    const interval = setInterval(() => {
      fetchScheduledScans();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const handleToggle = async (scanId) => {
    try {
      const response = await api.patch(`/api/v1/fno/scheduled-scans/${scanId}/toggle`);
      console.log('✅ Scan toggled:', response.data);
      
      // Update local state
      setScheduledScans(scheduledScans.map(scan => 
        scan.id === scanId ? response.data : scan
      ));
    } catch (err) {
      console.error('❌ Failed to toggle scan:', err);
      alert(`Failed to toggle scan: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDelete = async (scanId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled scan?')) {
      return;
    }

    try {
      await api.delete(`/api/v1/fno/scheduled-scans/${scanId}`);
      console.log('✅ Scan deleted:', scanId);
      
      // Remove from local state
      setScheduledScans(scheduledScans.filter(scan => scan.id !== scanId));
    } catch (err) {
      console.error('❌ Failed to delete scan:', err);
      alert(`Failed to delete scan: ${err.response?.data?.detail || err.message}`);
    }
  };

  const formatCronToTimes = (cronSchedule) => {
    try {
      // Parse cron (stored in UTC): "30 9,14 * * 1-5" -> Convert to IST times
      const parts = cronSchedule.split(' ');
      const utcMinute = parseInt(parts[0]);
      const utcHours = parts[1].split(',').map(h => parseInt(h));
      
      // Convert each UTC time to IST (UTC + 5:30)
      const istTimes = utcHours.map(utcHour => {
        let istHour = utcHour + 5;
        let istMinute = utcMinute + 30;
        
        // Handle minute overflow
        if (istMinute >= 60) {
          istMinute -= 60;
          istHour += 1;
        }
        
        // Handle hour overflow (day rollover)
        if (istHour >= 24) {
          istHour -= 24;
        }
        
        return `${istHour.toString().padStart(2, '0')}:${istMinute.toString().padStart(2, '0')}`;
      });
      
      return istTimes.join(', ');
    } catch (e) {
      return cronSchedule;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    // Display in IST timezone (Asia/Kolkata)
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
      hour12: true
    });
  };

  const isRecentlyExecuted = (lastRunAt) => {
    if (!lastRunAt) return false;
    const diff = new Date() - new Date(lastRunAt);
    return diff < 5 * 60 * 1000; // Within last 5 minutes
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center py-8">
          <FaSpinner className="animate-spin text-3xl text-blue-600" />
          <span className="ml-3 text-gray-600">Loading scheduled scans...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={fetchScheduledScans}
            className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
          >
            <FaSync /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-lg p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FaCalendarAlt className="text-2xl text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Scheduled Scans</h2>
            <p className="text-sm text-gray-600">
              Manage your automated F&O scans • Auto-refreshes every 30s
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            onClick={fetchScheduledScans}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Refresh Now"
          >
            <FaSync className="text-lg" />
          </button>
        </div>
      </div>

      {scheduledScans.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <FaClock className="text-4xl text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">No scheduled scans yet</p>
          <p className="text-sm text-gray-500">
            Click "Modify Scan" and enable scheduling to create your first automated scan
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {scheduledScans.map((scan) => {
              const executionMode = scan.parameters && scan.parameters.execution_mode ? scan.parameters.execution_mode : 'automatic';
              return (
                <motion.div
                key={scan.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`border-2 rounded-lg p-4 ${
                  scan.is_active 
                    ? scan.scan_type === 'breakout' 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-red-200 bg-red-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  {/* Left side - Scan Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {scan.scan_type === 'breakout' ? (
                        <FaArrowUp className="text-xl text-green-600" />
                      ) : (
                        <FaArrowDown className="text-xl text-red-600" />
                      )}
                      <h3 className="text-lg font-bold text-gray-900 capitalize">
                        {scan.scan_type} Scan
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        scan.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {scan.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        executionMode === 'manual' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {executionMode === 'manual' ? 'Manual (email)' : 'Automatic'}
                      </span>
                    </div>

                    {/* Execution Status Banner */}
                    {isRecentlyExecuted(scan.last_run_at) && (
                      <div className="mb-3 p-2 bg-green-100 border-l-4 border-green-500 rounded text-xs">
                        <div className="flex items-center gap-2">
                          <FaCheckCircle className="text-green-600" />
                          <span className="text-green-800 font-semibold">
                            Scan executed {Math.round((new Date() - new Date(scan.last_run_at)) / 1000 / 60)} minutes ago
                          </span>
                        </div>
                        <p className="text-green-700 mt-1 ml-5">
                          Scheduler is working! Results are being processed...
                        </p>
                      </div>
                    )}

                    {/* Schedule Info */}
                    <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                      <div>
                        <p className="text-gray-600 mb-1">Schedule Times (IST)</p>
                        <p className="font-semibold text-gray-900">
                          <FaClock className="inline mr-1" />
                          {formatCronToTimes(scan.cron_schedule)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-1">Last Run</p>
                        <p className={`font-medium ${isRecentlyExecuted(scan.last_run_at) ? 'text-green-600 font-bold' : 'text-gray-900'}`}>
                          {formatDate(scan.last_run_at)}
                          {isRecentlyExecuted(scan.last_run_at) && ' ✓'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-1">Next Run</p>
                        <p className="font-medium text-blue-700">
                          {formatDate(scan.next_run_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-1">Created</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(scan.created_at)}
                        </p>
                      </div>
                    </div>

                    {/* Parameters */}
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Parameters</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-gray-600">Momentum:</span>{' '}
                          <span className="font-semibold">
                            {scan.parameters.momentum_threshold_min}-{scan.parameters.momentum_threshold_max}%
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Option:</span>{' '}
                          <span className="font-semibold">{scan.parameters.option_moneyness}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Quantity:</span>{' '}
                          <span className="font-semibold">{scan.parameters.quantity} lot(s)</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Target:</span>{' '}
                          <span className="font-semibold text-green-600">+{scan.parameters.target_percent}%</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Stop-Loss:</span>{' '}
                          <span className="font-semibold text-red-600">-{scan.parameters.stop_loss_percent}%</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Budget:</span>{' '}
                          <span className="font-semibold">₹{scan.parameters.max_investment.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Last Scan Result */}
                    {scan.last_scan_result && (
                      <div className="mt-3 bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <p className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1">
                          <FaInfoCircle />
                          Latest Scan Result
                        </p>
                        {executionMode === 'manual' && (
                          <p className="text-xs text-blue-700 mb-2">
                            ✉️ Email {scan.last_scan_result.email_sent ? 'sent successfully' : 'pending/failed'} at {formatDate(scan.last_scan_result.executed_at)}
                          </p>
                        )}
                        
                        {/* Best Candidate Info */}
                        {scan.last_scan_result.best_candidate && (
                          <div className="mb-2 p-2 bg-white rounded border border-blue-200">
                            <p className="text-xs font-semibold text-gray-900 mb-1">
                              🏆 Stock Found: {scan.last_scan_result.best_candidate.symbol}
                            </p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-600">Change:</span>{' '}
                                <span className={`font-semibold ${scan.scan_type === 'breakout' ? 'text-green-600' : 'text-red-600'}`}>
                                  {scan.last_scan_result.best_candidate.pct_change}%
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Option:</span>{' '}
                                <span className="font-mono text-xs">{scan.last_scan_result.best_candidate.option_symbol}</span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Order Status */}
                        <div className="text-xs">
                          <span className="text-gray-700">Status: </span>
                          <span className={`font-semibold ${
                            scan.last_scan_result.success ? 'text-green-600' : 'text-orange-600'
                          }`}>
                            {scan.last_scan_result.message || 'Completed'}
                          </span>
                        </div>
                        
                        {/* Funds Info if available */}
                        {scan.last_scan_result.available !== undefined && (
                          <div className="mt-2 text-xs text-gray-700">
                            Available: ₹{scan.last_scan_result.available?.toLocaleString()} | 
                            Required: ₹{scan.last_scan_result.required?.toLocaleString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right side - Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => handleToggle(scan.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        scan.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                      title={scan.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {scan.is_active ? (
                        <FaToggleOn className="text-xl" />
                      ) : (
                        <FaToggleOff className="text-xl" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(scan.id)}
                      className="p-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default ScheduledScansSection;

